/**
 * The "Notater" section (Steg F6) at the bottom of the recipe read view:
 * a list of notes + a new-note form + a per-note like toggle. (The API,
 * DB tables and testids still use the original "comment" nomenclature — only
 * the user-facing Norwegian copy says "Notat".)
 *
 * Locked scope (see the F6 task): plain text comments, likes, delete. NO
 * tags/Fallgruve chips, NO "lagde den" marker, NO comment editing, NO
 * ratings/stars — those are deliberately cut from this slice.
 *
 * Authorization (UI mirror only — the API + Postgres RLS are authoritative):
 *  - Commenting and liking are NOT role-gated — every cookbook member may do
 *    both (mirrors `RecipeComment`'s docs in @tjommi/shared).
 *  - The delete affordance is shown only for the comment's own author, or for
 *    the cookbook OWNER (moderation) — never for any other member.
 *
 * Security: a comment's `body` is rendered as a plain React text node, NEVER
 * via `dangerouslySetInnerHTML` — the API returns it verbatim, so React's
 * auto-escaping is the only thing standing between a comment and stored XSS.
 */

import type { RecipeComment } from "@tjommi/shared";
import React, { useEffect, useState } from "react";

import {
  NotFoundError,
  UnauthorizedError,
  createComment,
  deleteComment,
  fetchComments,
  likeComment,
  unlikeComment,
} from "../api";
import { formatRelativeTime } from "../lib/formatDate";
import { initialsFromName } from "../lib/initials";

type ListState =
  | { status: "loading" }
  | { status: "ok"; comments: RecipeComment[] }
  | { status: "error"; message: string };

/** A best-effort, null-safe author label — never the raw subject id. */
function authorLabel(displayName: string | null): string {
  return displayName?.trim() || "Medlem";
}

export function RecipeComments({
  recipeId,
  accessToken,
  mySubject,
  canModerate,
  onSessionExpired,
}: {
  recipeId: string;
  accessToken: string;
  /** The viewer's own Zitadel subject — used ONLY to gate the delete affordance. */
  mySubject: string;
  /** True when the viewer is the cookbook OWNER (may delete any comment). */
  canModerate: boolean;
  onSessionExpired: () => void;
}): React.JSX.Element {
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    setState({ status: "loading" });

    fetchComments(recipeId, accessToken)
      .then((comments) => {
        if (!ignore) setState({ status: "ok", comments });
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (err instanceof UnauthorizedError) {
          onSessionExpired();
          return;
        }
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "Ukjent feil",
        });
      });

    return () => {
      ignore = true;
    };
  }, [recipeId, accessToken]);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const body = draft.trim();
    if (!body || submitting) return; // matches the prototype's `if(!text) return`

    setSubmitting(true);
    setFormError(null);
    try {
      const created = await createComment(recipeId, body, accessToken);
      setState((prev) =>
        prev.status === "ok" ? { status: "ok", comments: [...prev.comments, created] } : prev,
      );
      setDraft("");
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      setFormError("Kunne ikke sende notatet. Prøv igjen.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleLike(comment: RecipeComment): Promise<void> {
    const wasLiked = comment.likedByMe;
    const prevCount = comment.likeCount;

    // Optimistic update first — a kitchen-usability affordance, reverted below on error.
    setState((prev) =>
      prev.status === "ok"
        ? {
            status: "ok",
            comments: prev.comments.map((c) =>
              c.id === comment.id
                ? { ...c, likedByMe: !wasLiked, likeCount: prevCount + (wasLiked ? -1 : 1) }
                : c,
            ),
          }
        : prev,
    );

    try {
      if (wasLiked) await unlikeComment(comment.id, accessToken);
      else await likeComment(comment.id, accessToken);
    } catch (err) {
      // Revert on any failure.
      setState((prev) =>
        prev.status === "ok"
          ? {
              status: "ok",
              comments: prev.comments.map((c) =>
                c.id === comment.id ? { ...c, likedByMe: wasLiked, likeCount: prevCount } : c,
              ),
            }
          : prev,
      );
      if (err instanceof UnauthorizedError) onSessionExpired();
    }
  }

  async function handleDelete(comment: RecipeComment): Promise<void> {
    if (!window.confirm("Slette dette notatet? Dette kan ikke angres.")) return;

    try {
      await deleteComment(comment.id, accessToken);
      setState((prev) =>
        prev.status === "ok"
          ? { status: "ok", comments: prev.comments.filter((c) => c.id !== comment.id) }
          : prev,
      );
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      if (err instanceof NotFoundError) {
        // Already gone (or no longer permitted) — treat as gone.
        setState((prev) =>
          prev.status === "ok"
            ? { status: "ok", comments: prev.comments.filter((c) => c.id !== comment.id) }
            : prev,
        );
        return;
      }
      setFormError("Kunne ikke slette notatet. Prøv igjen.");
    }
  }

  /** Auto-grow the textarea, mirroring the prototype's plain-JS behaviour. */
  function autoGrow(el: HTMLTextAreaElement): void {
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }

  return (
    <section className="read-card" data-testid="comments-section">
      <div className="read-card-h">
        <h2>Notater</h2>
      </div>

      {state.status === "loading" && (
        <p data-testid="comments-loading" className="cmt-meta">
          Laster notater …
        </p>
      )}

      {state.status === "error" && (
        <p data-testid="comments-error" className="cmt-meta" role="alert">
          Kunne ikke laste notater. Prøv å laste siden på nytt.
        </p>
      )}

      {state.status === "ok" && (
        <>
          {state.comments.length === 0 && (
            <p data-testid="comments-empty" className="cmt-meta">
              Ingen notater ennå — skriv gjerne det første.
            </p>
          )}

          <div data-testid="comment-list">
            {state.comments.map((comment) => {
              const canDelete = comment.authorId === mySubject || canModerate;
              const name = authorLabel(comment.authorDisplayName);
              return (
                <div className="cmt" key={comment.id} data-testid={`comment-${comment.id}`}>
                  <div className="cmt-av" aria-hidden="true">
                    {initialsFromName(comment.authorDisplayName)}
                  </div>
                  <div className="cmt-body">
                    <div className="cmt-head">
                      <b data-testid={`comment-author-${comment.id}`}>{name}</b>
                      <span className="cmt-meta">{formatRelativeTime(comment.createdAt)}</span>
                    </div>
                    <p className="cmt-text" data-testid={`comment-body-${comment.id}`}>
                      {comment.body}
                    </p>
                    <div className="cmt-foot">
                      <button
                        type="button"
                        className="cmt-like"
                        data-testid={`comment-like-${comment.id}`}
                        aria-pressed={comment.likedByMe}
                        onClick={() => void handleToggleLike(comment)}
                      >
                        {comment.likedByMe ? "♥" : "♡"} {comment.likeCount}
                      </button>
                      {canDelete && (
                        <button
                          type="button"
                          className="cmt-like"
                          data-testid={`comment-delete-${comment.id}`}
                          onClick={() => void handleDelete(comment)}
                        >
                          Slett
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <form
            className="cmt-new"
            data-testid="comment-new-form"
            onSubmit={(e) => void handleSubmit(e)}
          >
            <div className="cmt-new-field">
              <textarea
                className="sub-input"
                data-testid="comment-new-textarea"
                placeholder="Skriv et notat …"
                rows={2}
                value={draft}
                disabled={submitting}
                onChange={(e) => setDraft(e.target.value)}
                onInput={(e) => autoGrow(e.currentTarget)}
              />
              {formError && (
                <p data-testid="comment-form-error" role="alert" className="cmt-meta">
                  {formError}
                </p>
              )}
              <div className="cmt-new-foot">
                <button
                  type="submit"
                  className="btn btn-primary"
                  data-testid="comment-submit"
                  disabled={submitting || draft.trim() === ""}
                >
                  {submitting ? "Lagrer …" : "Lagre notat"}
                </button>
              </div>
            </div>
          </form>
        </>
      )}
    </section>
  );
}
